import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MailIcon, UserIcon, UserPlusIcon, InfoIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/app/store/authStore';
import { Spinner } from '@/shared/components/Spinner';

const addUserSchema = z.object({
  email: z.string().email('Invalid email address'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
});

type AddUserFormData = z.infer<typeof addUserSchema>;

export const AddUserForm: React.FC = () => {
  const provisionUser = useAuthStore((s) => s.provisionUser);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddUserFormData>({
    resolver: zodResolver(addUserSchema),
  });

  const onSubmit = async (data: AddUserFormData) => {
    setIsLoading(true);
    try {
      const errorMessage = await provisionUser(data.email, data.firstName, data.lastName);

      if (errorMessage === null) {
        toast.success(`Invitation sent to ${data.email}`);
        reset();
      } else {
        toast.error(errorMessage || 'Failed to create user');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl animate-fade-in">
      <div className="mb-6 flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <UserPlusIcon className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Add user</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Provision a new account for the team.
          </p>
        </div>
      </div>

      <div className="mb-6 flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/10 p-4">
        <InfoIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <p className="text-sm text-foreground">
          The new user receives an activation email to confirm their address and set their own password.
          Accounts start with standard (non-admin) access.
        </p>
      </div>

      <form className="card space-y-6 p-8" onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="label">
                First name
              </label>
              <div className="relative mt-1.5">
                <UserIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  {...register('firstName')}
                  id="firstName"
                  type="text"
                  autoComplete="given-name"
                  className="input pl-9"
                  placeholder="First name"
                />
              </div>
              {errors.firstName && (
                <p className="mt-1 text-sm text-danger">{errors.firstName.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="lastName" className="label">
                Last name
              </label>
              <div className="relative mt-1.5">
                <UserIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  {...register('lastName')}
                  id="lastName"
                  type="text"
                  autoComplete="family-name"
                  className="input pl-9"
                  placeholder="Last name"
                />
              </div>
              {errors.lastName && (
                <p className="mt-1 text-sm text-danger">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="email" className="label">
              Email address
            </label>
            <div className="relative mt-1.5">
              <MailIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                {...register('email')}
                id="email"
                type="email"
                autoComplete="off"
                className="input pl-9"
                placeholder="you@company.com"
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-sm text-danger">{errors.email.message}</p>
            )}
          </div>
        </div>

        <button type="submit" disabled={isLoading} className="btn-primary w-full">
          {isLoading && <Spinner className="text-primary-foreground" />}
          {isLoading ? 'Sending invitation…' : 'Send invitation'}
        </button>
      </form>
    </div>
  );
};
